import type { Message } from '../hooks/useChat.ts';
import { sourceLines, toolLabel } from '../lib/format.ts';
import { Markdown } from '../lib/markdown.tsx';

const errorText: Record<NonNullable<Message['error']>, string> = {
  network: 'Se ha cortado la conexión. Comprueba la cobertura e inténtalo de nuevo.',
  rate_limit: 'Demasiadas preguntas seguidas. Espera un minuto y vuelve a intentarlo.',
  disabled: 'El asistente no está disponible ahora mismo.',
  server: 'No he podido responder ahora mismo. Inténtalo de nuevo en un momento.',
  answer: '',
};

export function MessageView({ message, onRetry }: { message: Message; onRetry?: () => void }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] rounded-2xl rounded-br-md bg-burgundy px-4 py-2.5 text-lg leading-snug whitespace-pre-wrap text-cream">
          {message.text}
        </p>
      </div>
    );
  }

  const waiting = message.status === 'streaming' && !message.text;
  const failed = message.status === 'error';
  return (
    <article
      className="max-w-full text-lg leading-relaxed"
      aria-busy={message.status === 'streaming'}
    >
      {waiting && (
        <p className="flex items-center gap-2 text-ink-soft dark:text-cream/75">
          <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-purple dark:bg-gold-300" />
          {message.tool ? toolLabel(message.tool) : 'Pensando…'}
        </p>
      )}
      {message.text && <Markdown text={message.text} />}
      {failed && message.error !== 'answer' && (
        <p className="text-burgundy dark:text-burgundy-300">
          {errorText[message.error ?? 'server']}
        </p>
      )}
      {failed && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 rounded-full border-2 border-purple px-4 py-1.5 font-semibold text-purple hover:bg-purple hover:text-cream dark:border-gold-300 dark:text-gold-300 dark:hover:bg-gold-300 dark:hover:text-ink"
        >
          Reintentar
        </button>
      )}
      {message.status === 'done' && (message.sources?.length || message.revised) && (
        <footer className="mt-2 space-y-0.5 border-t border-ink/10 pt-1.5 text-sm text-ink-soft dark:border-cream/15 dark:text-cream/70">
          {sourceLines(message.sources ?? []).map((line) => (
            <p key={line.text} title={line.detail}>
              Fuente: {line.text}
            </p>
          ))}
          {message.revised && <p>Respuesta revisada tras comprobar los datos.</p>}
        </footer>
      )}
    </article>
  );
}
